const CACHE_KEY = "rodhak.public.activeTrips.v2";
const CACHE_TTL_MS = 5 * 60 * 1000;
const STALE_TRIP_MS = 60 * 1000;

const toTimestamp = (time) => (typeof time === "number" ? time : Date.parse(time));

export const VIEW_MODES = {
  ALL_TRIPS: "ALL_TRIPS",
  SEARCH_RESULTS: "SEARCH_RESULTS",
  SELECTED_TRIP: "SELECTED_TRIP",
  DEDICATED_TRACKING: "DEDICATED_TRACKING",
};

export const getTripId = (trip) => trip?._id || trip?.tripId || trip?.tripID;

export const normalizeLngLat = (coord) => {
  if (!Array.isArray(coord) || coord.length !== 2) return null;
  const a = Number(coord[0]);
  const b = Number(coord[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (Math.abs(a) > 90) return [a, b];
  if (Math.abs(b) > 90) return [b, a];
  return [b, a];
};

export const getTripCoordinates = (trip) => {
  if (trip?.currentLocation?.coordinates?.length === 2) {
    const lng = Number(trip.currentLocation.coordinates[0]);
    const lat = Number(trip.currentLocation.coordinates[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
  }

  const lng = Number(trip?.longitude);
  const lat = Number(trip?.latitude);
  if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];

  return null;
};

export const normalizeTrip = (trip) => {
  if (!trip) return null;
  const id = getTripId(trip);
  const coordinates = getTripCoordinates(trip);
  if (!id || !coordinates) return null;

  const source = trip.Start || trip.sourceLocation || trip.from || "Unknown";
  const destination = trip.End || trip.destinationLocation || trip.to || "Unknown";
  const vehicle = trip.Vehicle || trip.vehicleNum || trip.vehicle || "Bus";
  const now = new Date().toISOString();

  return {
    ...trip,
    _id: id,
    tripId: id,
    Vehicle: vehicle,
    vehicleNum: vehicle,
    Start: source,
    End: destination,
    Type: trip.Type || trip.type || "Bus",
    sourceLocation: source,
    destinationLocation: destination,
    viaLocation: trip.viaLocation || trip.viaRoute || "",
    longitude: coordinates[0],
    latitude: coordinates[1],
    currentTime: trip.currentTime || now,
    currentLocation: {
      type: "Point",
      coordinates,
    },
    status: trip.status || "live",
    speed: Number.isFinite(Number(trip.speed)) ? Number(trip.speed) : 0,
  };
};

export const normalizeTrips = (trips) =>
  (Array.isArray(trips) ? trips : Object.values(trips || {}))
    .map(normalizeTrip)
    .filter(Boolean)
    .filter((trip) => {
      const ts = toTimestamp(trip.currentTime);
      return Number.isNaN(ts) || Date.now() - ts < STALE_TRIP_MS;
    });

export const mergeTripsById = (...tripLists) => {
  const byId = {};
  tripLists.flat().forEach((trip) => {
    const normalized = normalizeTrip(trip);
    if (normalized) byId[normalized._id] = { ...byId[normalized._id], ...normalized };
  });
  return byId;
};

export const searchTrips = (trips, query) => {
  const from = (query?.from || "").trim().toLowerCase();
  const to = (query?.to || "").trim().toLowerCase();
  const text = (query?.text || "").trim().toLowerCase();
  const type = (query?.type || "all").toLowerCase();

  return trips.filter((trip) => {
    const start = (trip.Start || "").toLowerCase();
    const end = (trip.End || "").toLowerCase();
    const vehicle = (trip.Vehicle || "").toLowerCase();
    const tripType = (trip.Type || "").toLowerCase();
    const via = (trip.viaLocation || trip.viaRoute || "").toLowerCase();

    const fromMatch = from ? start.includes(from) || via.includes(from) : true;
    const toMatch = to ? end.includes(to) || via.includes(to) : true;
    const textMatch = text
      ? [start, end, vehicle, tripType, via].some((field) => field.includes(text))
      : true;
    const typeMatch = type === "all" ? true : tripType.includes(type);

    return fromMatch && toMatch && textMatch && typeMatch;
  });
};

export const cacheTrips = (trips) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), trips: normalizeTrips(trips) }),
    );
  } catch {
    // Cache is best effort only.
  }
};

export const readCachedTrips = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (!cached || Date.now() - cached.savedAt > CACHE_TTL_MS) return [];
    return normalizeTrips(cached.trips);
  } catch {
    return [];
  }
};

export const formatLastUpdated = (time) => {
  const ts = toTimestamp(time);
  if (Number.isNaN(ts)) return "just now";
  const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m ago`;
};
